import React, {
    forwardRef,
    useCallback,
    useContext,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ScrollView,
    View,
    StyleProp,
    ViewStyle,
    StyleSheet,
    NativeScrollEvent,
    NativeSyntheticEvent,
    ScrollViewProps,
    Platform,
    LayoutChangeEvent,
} from "react-native";
import commonStyles from "./common-style";
import { BlurView as ExpoBlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";
import MaskedView from "@react-native-masked-view/masked-view";
import Button, { BUTTON_SHRINK_DURATION, ButtonMode, ButtonProps } from "../components/Button";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import VStack from "../ui_atoms/VStack";
import ScreenTitle from "../components/ScreenTitle";
import { RefreshControl } from "../components/RefreshControl/RefreshControl.web";
import { useRefresh } from "../hooks/refresh";
import { IconName } from "../components/Icon";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withSequence,
    withTiming,
} from "react-native-reanimated";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { ScreenHeaderManagerContext } from "../contexts/ScreenHeaderManager";
import { XOR } from "ts-essentials";
import {
    KeyboardAwareScrollView,
    useReanimatedKeyboardAnimation,
} from "react-native-keyboard-controller";
import Loading from "../components/Loading";
import ScreenCardHeadband, { ScreenCardHeadbandProps } from "./ScreenCardHeadband";
import { LinearGradientConfig } from "../configs/linear-gradients";
import HStack from "../ui_atoms/HStack";
import Gap from "../ui_atoms/Gap";
import IconButton from "../components/IconButton";
import { useActionSheetOrDropdown } from "../hooks/use-action-sheet-or-dropdown";

const AnimatedView = Animated.createAnimatedComponent(View);

const BUTTON_BACKGROUND_BLUR_CONFIG = {
    0: "rgba(255,255,255,0)",
    0.2: "rgba(255,255,255,0.8)",
    0.8: "rgba(255,255,255,1)",
    1: "rgba(255,255,255,1)",
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const {
    locations: BUTTON_BACKGROUND_BLUR_LOCATIONS,
    colors: BUTTON_BACKGROUND_BLUR_COLORS,
}: LinearGradientConfig = Object.keys(BUTTON_BACKGROUND_BLUR_CONFIG)
    .sort((a, b) => Number(a) - Number(b))
    .reduce(
        (result, key) => ({
            locations: [...result.locations, Number(key)],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            colors: [...result.colors, BUTTON_BACKGROUND_BLUR_CONFIG[key]],
        }),
        {
            locations: [] as number[],
            colors: [] as string[],
        },
    );

export type ButtonType = {
    title?: string;
    mode?: ButtonMode;
    isValid?: boolean;
    validating?: boolean;
} & Pick<ButtonProps, "onPress" | "size" | "testID" | "style" | "textSize">;

export type Pagination = {
    isLoading: boolean;
    fetchMore: () => Promise<void>;
    hasNextPage: boolean;
};

export type ScreenCardOptionalAction = {
    icon?: IconName;
    onPress: () => void | Promise<void>;
    label: string;
};

export type Props = ScrollViewProps & {
    title?: string;
    isContentContainerFlex?: boolean;
    style?: StyleProp<ViewStyle>;
    contentStyle?: StyleProp<ViewStyle>;
    shrinkButtonToIcon?: IconName;
    slideButtonUp?: boolean;
    refresh?: () => Promise<unknown>;
    headerComponent?: React.ReactNode;
    children?: React.ReactNode | ((screenActionsHeight: number) => React.ReactNode);
    pagination?: Pagination;
    headband?: ScreenCardHeadbandProps;
    noBottomBlur?: boolean;
    backgroundGradient?: LinearGradientConfig;
    optionalActions?: ScreenCardOptionalAction[];
} & XOR<{ buttons?: ButtonType[] | undefined | false }, { bottomComponent: React.ReactNode }>;

const BUTTON_SLIDEUP_DURATION = 400;

export type ScreenCardHandle = {
    scrollContentToEnd: () => void;
    scrollContentTo: (y: number, x?: number, animated?: boolean) => void;
    isScrolled: boolean;
};

// eslint-disable-next-line react/display-name
const ScreenCard = forwardRef<ScreenCardHandle, Props>(
    (
        {
            title,
            isContentContainerFlex,
            buttons,
            children,
            style,
            contentStyle,
            shrinkButtonToIcon,
            slideButtonUp,
            refresh,
            headerComponent,
            bottomComponent,
            pagination,
            headband,
            noBottomBlur,
            backgroundGradient,
            optionalActions,
            ...props
        },
        ref,
    ) => {
        const i18n = useI18n();
        const { gridSize, marginSize } = useTheme();
        const isLarge = useIsScreenSizeMin("large");
        const { localRefresh, refreshing } = useRefresh({ refresh });
        const { setIsScreenScrolled } = useContext(ScreenHeaderManagerContext);

        const noButtons = useMemo(
            () => !bottomComponent && (buttons || []).length === 0,
            [bottomComponent, buttons],
        );

        const [isScrolled, setIsScrolled] = useState(false);

        const [nextPageLoadingHeight, setNextPageLoadingHeight] = useState(0);

        const blurOpacity = useSharedValue(1);

        const isPaginationLoading = useRef(false);

        const onScroll = useCallback(
            (e: NativeSyntheticEvent<NativeScrollEvent>) => {
                if (pagination?.hasNextPage && !isPaginationLoading.current) {
                    const {
                        contentSize: { height: contentSizeHeight },
                        contentOffset: { y: contentOffsetY },
                        layoutMeasurement: { height: layoutHeight },
                    } = e.nativeEvent;
                    const scrollPosition = contentOffsetY + layoutHeight;

                    if (scrollPosition > contentSizeHeight - nextPageLoadingHeight) {
                        isPaginationLoading.current = true;
                        void pagination.fetchMore().then(() => {
                            setTimeout(() => {
                                isPaginationLoading.current = false;
                            }, 0);
                        });
                    }
                }

                props.onScroll?.(e);
                const _isScrolled = e.nativeEvent.contentOffset.y > 0;
                setIsScrolled(_isScrolled);
                setIsScreenScrolled(_isScrolled);

                if (slideButtonUp) {
                    blurOpacity.value = withTiming(_isScrolled ? 0 : 1, {
                        duration: BUTTON_SHRINK_DURATION,
                    });
                }
            },
            [
                blurOpacity,
                nextPageLoadingHeight,
                pagination,
                props,
                setIsScreenScrolled,
                slideButtonUp,
            ],
        );

        const isButtonShrinked = useMemo(() => isScrolled && !isLarge, [isLarge, isScrolled]);

        // By default we hide the slideButtonUp way below until we can calculate its height for animation
        const translateY = useSharedValue(1000);

        const animatedScreenActionsStyles = useAnimatedStyle(() => ({
            transform: [{ translateY: translateY.value }],
        }));

        const animatedBlurStyle = useAnimatedStyle(() => ({
            opacity: blurOpacity.value,
        }));

        const [screenActionsHeight, setScreenActionsHeight] = useState(0);

        const onScreenBottomLayout = useCallback(
            (e: LayoutChangeEvent) => {
                const height = e.nativeEvent.layout.height;
                if (height) {
                    setScreenActionsHeight(height);
                    if (slideButtonUp && !noButtons) {
                        translateY.value = withSequence(
                            withTiming(height, {
                                duration: 0,
                            }),
                            withTiming(0, {
                                duration: BUTTON_SLIDEUP_DURATION,
                            }),
                        );
                    }
                }
            },
            [noButtons, setScreenActionsHeight, slideButtonUp, translateY],
        );

        const scrollViewRef = useRef<ScrollView>(null);

        const scrollContentToEnd = useCallback(() => {
            scrollViewRef.current?.scrollToEnd();
        }, []);

        const scrollContentTo = useCallback((y: number, x?: number, animated: boolean = true) => {
            scrollViewRef.current?.scrollTo({
                y,
                x,
                animated,
            });
        }, []);

        const isChildrenFunction = useMemo(() => typeof children === "function", [children]);

        useImperativeHandle(ref, () => ({ scrollContentToEnd, scrollContentTo, isScrolled }));

        const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

        const { onPress: openActionSheet, ref: actionSheetRef } = useActionSheetOrDropdown({
            items:
                optionalActions?.map((action) => ({
                    label: action.label,
                    onClick: action.onPress,
                    icon: action.icon,
                })) || [],
            position: "left",
        });

        if (optionalActions && shrinkButtonToIcon) {
            console.warn("Optional actions are not supported when shrinkButtonToIcon is enabled.");
        }
        return (
            <>
                {backgroundGradient ? (
                    <LinearGradient {...backgroundGradient} style={StyleSheet.absoluteFill} />
                ) : null}
                <KeyboardAwareScrollView
                    ref={scrollViewRef}
                    bottomOffset={screenActionsHeight}
                    style={[{ flex: 1 }, style]}
                    {...(isContentContainerFlex
                        ? {
                              contentContainerStyle: {
                                  flexGrow: 1,
                                  paddingBottom: !isChildrenFunction
                                      ? screenActionsHeight
                                      : undefined,
                              },
                          }
                        : {
                              contentContainerStyle: {
                                  paddingBottom: !isChildrenFunction
                                      ? screenActionsHeight
                                      : undefined,
                              },
                          })}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets
                    scrollEventThrottle={0}
                    refreshControl={
                        localRefresh && (
                            <RefreshControl onRefresh={localRefresh} refreshing={refreshing} />
                        )
                    }
                    {...props}
                    onScroll={onScroll}
                >
                    {headband ? <ScreenCardHeadband {...headband} /> : null}
                    {headerComponent}
                    <View style={[commonStyles.screenContainer]}>
                        <View style={[commonStyles.screenContent, contentStyle]}>
                            {title ? (
                                <ScreenTitle
                                    style={{
                                        marginTop: 0,
                                        paddingHorizontal: marginSize,
                                        paddingVertical: gridSize / 2,
                                    }}
                                >
                                    {title}
                                </ScreenTitle>
                            ) : null}
                            {typeof children === "function"
                                ? children(screenActionsHeight)
                                : children}
                            {pagination?.hasNextPage ? (
                                <VStack
                                    v={0.5}
                                    onLayout={(e) => {
                                        setNextPageLoadingHeight(e.nativeEvent.layout.height);
                                    }}
                                >
                                    <Loading />
                                </VStack>
                            ) : null}
                        </View>
                    </View>
                </KeyboardAwareScrollView>
                {!noButtons && (
                    <AnimatedView
                        style={[
                            commonStyles.screenActions,
                            slideButtonUp && animatedScreenActionsStyles,
                            { transform: [{ translateY: keyboardHeight }] },
                        ]}
                        onLayout={onScreenBottomLayout}
                    >
                        <VStack
                            style={[
                                { width: "100%" },
                                { paddingHorizontal: gridSize, paddingVertical: gridSize },
                                shrinkButtonToIcon &&
                                    !isLarge && {
                                        width: "auto",
                                        backgroundColor: "transparent",
                                        position: "absolute",
                                        alignSelf: "flex-end",
                                        bottom: 0,
                                    },
                            ]}
                        >
                            <AnimatedView style={[StyleSheet.absoluteFill, animatedBlurStyle]}>
                                {!noBottomBlur ? (
                                    Platform.OS === "ios" ? (
                                        <MaskedView
                                            maskElement={
                                                <LinearGradient
                                                    locations={BUTTON_BACKGROUND_BLUR_LOCATIONS}
                                                    colors={BUTTON_BACKGROUND_BLUR_COLORS}
                                                    style={StyleSheet.absoluteFill}
                                                />
                                            }
                                            style={StyleSheet.absoluteFill}
                                        >
                                            <ExpoBlurView
                                                intensity={10}
                                                style={StyleSheet.absoluteFill}
                                            />
                                        </MaskedView>
                                    ) : (
                                        <LinearGradient
                                            locations={BUTTON_BACKGROUND_BLUR_LOCATIONS}
                                            colors={BUTTON_BACKGROUND_BLUR_COLORS}
                                            style={[StyleSheet.absoluteFill, { opacity: 0.9 }]}
                                        />
                                    )
                                ) : null}
                            </AnimatedView>
                            {shrinkButtonToIcon ? (
                                bottomComponent ||
                                (buttons || []).map((button, index) => (
                                    <Button
                                        key={index}
                                        buttonMode={
                                            button.mode || (index === 1 ? "outlined" : undefined)
                                        }
                                        style={[{ width: "100%" }, index > 0 && { marginTop: 0 }]}
                                        size="large"
                                        disabled={
                                            button.isValid === undefined ? false : !button.isValid
                                        }
                                        processing={button.validating}
                                        label={
                                            button.title ||
                                            i18n.t(
                                                "screens.auth.screen_card.validation_button_title",
                                            )
                                        }
                                        onPress={button.onPress}
                                        shrinkToIcon={shrinkButtonToIcon}
                                        shrinked={isButtonShrinked}
                                        {...button}
                                    />
                                ))
                            ) : (
                                <HStack alignment="top">
                                    <VStack gap={0.33} style={{ flex: 1 }}>
                                        {bottomComponent ||
                                            (buttons || []).map((button, index) => (
                                                <Button
                                                    key={index}
                                                    buttonMode={
                                                        button.mode ||
                                                        (index === 1 ? "outlined" : undefined)
                                                    }
                                                    style={[
                                                        { alignSelf: "stretch" },
                                                        index > 0 && { marginTop: 0 },
                                                    ]}
                                                    size="large"
                                                    disabled={
                                                        button.isValid === undefined
                                                            ? false
                                                            : !button.isValid
                                                    }
                                                    processing={button.validating}
                                                    label={
                                                        button.title ||
                                                        i18n.t(
                                                            "screens.auth.screen_card.validation_button_title",
                                                        )
                                                    }
                                                    onPress={button.onPress}
                                                    shrinkToIcon={shrinkButtonToIcon}
                                                    shrinked={isButtonShrinked}
                                                    {...button}
                                                />
                                            ))}
                                    </VStack>
                                    {optionalActions && optionalActions.length > 0 && (
                                        <>
                                            <Gap gap={0.5} />
                                            <IconButton
                                                icon="menu"
                                                mode="outlined"
                                                size="large"
                                                onPress={openActionSheet}
                                                ref={actionSheetRef}
                                            />
                                        </>
                                    )}
                                </HStack>
                            )}
                        </VStack>
                    </AnimatedView>
                )}
            </>
        );
    },
);

export default ScreenCard;
